import {
  OBAddress,
  OBShippingAddressInfo,
  StrictSetFieldValue,
} from '../../../../types';
import * as React from 'react';
import { useFormikContext } from 'formik';
import { AddressFormInputs } from './AddressFormInputs';
import { SiteSelect } from './SiteSelect';
import { useSelector } from 'react-redux';
import { Panel } from '@rtkwlf/ui';
import { onboardingSelector } from '../../../../reducers/onboarding';
import styled from 'styled-components';

const RadioContainer = styled.div`
  display: flex;
  margin-bottom: 15px;
  align-items: center;
`;

const SiteSelectContainer = styled.div`
  width: 100%;
  margin-left: 5px;
`;

const AddressLabel = styled.div`
  margin-left: 5px;
`;

const ShippingAddressPanel: typeof Panel = styled(Panel)`
  margin-left: 0px;
  margin-right: 0px;
  margin-top: 20px;
`;

/**
 * A list of address field names. Note all fields are text fields.
 */
export const ADDRESS_FIELDS: Array<keyof Omit<OBAddress, 'id'>> = [
  'country',
  'street',
  'street2',
  'city',
  'region',
  'postcode',
];

type FormValues = Omit<OBShippingAddressInfo, 'shippingSiteLocation'> &
  Omit<OBAddress, 'id'>;

/**
 * A collection of shipping address-related form input fields.
 * Allows toggling between using existing site & adding new site.
 * Must be used within a formik context with the following fields:
 *  - isShippingToNewSite
 *  - shippingSiteId
 *  - country
 *  - street
 *  - street2
 *  - city
 *  - region
 *  - postcode
 */
export const ShippingAddressFormInputs: React.FunctionComponent = () => {
  const formikContext = useFormikContext<FormValues>();
  const { values, setFieldTouched, isSubmitting } = formikContext;
  const setFieldValue: StrictSetFieldValue<FormValues> =
    formikContext.setFieldValue;
  const { shippingSiteId, isShippingToNewSite } = values;

  const sites = useSelector(onboardingSelector.getSites);

  React.useEffect(() => {
    if (!isShippingToNewSite) {
      // populate the alternative address with the selected existing site
      const existingSite = sites[shippingSiteId];
      if (existingSite) {
        ADDRESS_FIELDS.forEach((field) => {
          setFieldValue(field, existingSite.address[field]);
        });
      }
    }
  }, [setFieldValue, sites, shippingSiteId, isShippingToNewSite]);

  const onShipToNewSite = React.useCallback(() => {
    setFieldValue('isShippingToNewSite', true);
    setFieldValue('shippingSiteId', '');
    // clear the alternative address
    ADDRESS_FIELDS.forEach((field) => {
      setFieldValue(field, '');
      setFieldTouched(field, false);
    });
  }, [setFieldTouched, setFieldValue]);
  const onShipToExistingSite = React.useCallback(() => {
    setFieldValue('isShippingToNewSite', false);
    ADDRESS_FIELDS.forEach((field) => {
      setFieldValue(field, '');
      setFieldTouched(field, false);
    });
  }, [setFieldValue, setFieldTouched]);

  return (
    <ShippingAddressPanel title="Shipping Address" titleLevel={5}>
      <RadioContainer className="radio-button-group">
        <input
          type="radio"
          name="isNotShippingToNewSite"
          checked={!isShippingToNewSite}
          onChange={onShipToExistingSite}
          disabled={isSubmitting}
        />
        <SiteSelectContainer>
          <SiteSelect
            name="shippingSiteId"
            id="selectShippingSite"
            disabled={isShippingToNewSite || isSubmitting}
            className="radio-button-select"
            includeFinalized
          />
        </SiteSelectContainer>
      </RadioContainer>
      <div className="radio-button-group">
        <RadioContainer>
          <input
            type="radio"
            name="isShippingToNewSite"
            checked={isShippingToNewSite}
            onChange={onShipToNewSite}
            disabled={isSubmitting}
          />
          <AddressLabel>Another Address</AddressLabel>
        </RadioContainer>
        <AddressFormInputs
          disabled={!isShippingToNewSite || isSubmitting}
          showShippingNotification={true}
        />
      </div>
    </ShippingAddressPanel>
  );
};
